/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../IEditor";
import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { createEventEmitter, emitEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { SessionStorageParamsResolver } from "./Resolvers/SessionStorageParamsResolver";
import { ISessionStorageType } from "./SessionStorageServiceTypes";
import { BP, importSchema, ISchemaConstObject, TSchemaConstObjectProps } from "@adapptio/lib-blueprint";
import { ErrorTracker } from "@adapptio/lib-components";

export interface ISessionStorageServiceBaseResolvers {
	sessionStorageParams: SessionStorageParamsResolver;
}

export class SessionStorageService {
	/** Logger facility */
	private logger: BrowserLogger;

	/* Dependency services */
	private apiService: IEditorApiService;

	/** Session storages */
	private sessionStorage: ISessionStorageType[] = [];

	/** Index of session storage name -> type object */
	private typeIndexByName: { [K: string]: ISessionStorageType } = {};

	/** Emitted when session storages have changed or reloaded */
	public onSessionStorageChange = createEventEmitter<void>();

	/** Cached base resolver */
	private baseResolvers: ISessionStorageServiceBaseResolvers;

	/**
	 * Service constructor
	 *
	 * @param dc Dependency Container
	 */
	constructor(dc: IEditor) {
		this.logger = dc.get("logger").facility("SessionStorage");

		this.apiService = dc.get("apiService");

		this.createBaseResolvers();
	}

	/**
	 * Initializes the service
	 */
	public async init(): Promise<void> {
		await this.loadSessionStorage();
	}

	/**
	 * Disposes the service
	 */
	public dispose(): void {
		removeAllEventListeners(this.onSessionStorageChange);
	}

	/**
	 * Loads session storages from the API
	 */
	private async loadSessionStorage(): Promise<void> {
		const errorTracker = ErrorTracker.getInstance();

		try {
			const apiRes = await this.apiService.getManifestDefinition();

			this.sessionStorage = [];
			this.typeIndexByName = {};

			apiRes.sessionStorages.forEach((item) => {
				try {
					// Create session storage object
					const typeObject: ISessionStorageType = {
						name: item.name,
						category: item.category,
						optionsSchema: BP.Const.Map({
							value: importSchema<ISchemaConstObject<TSchemaConstObjectProps>>(
								item.optionsSchema
							)
						})
					};

					// Add to indexes
					this.sessionStorage.push(typeObject);
					this.typeIndexByName[typeObject.name] = typeObject;
				} catch (err) {
					errorTracker.captureException(err);

					this.logger.error(`Failed to import session storage "${item.integrationType}":`, err);
				}
			});

			this.logger.debug("Session storages loaded:", this.sessionStorage);
			emitEvent(this.onSessionStorageChange);
		} catch (err) {
			errorTracker.captureException(err);

			this.logger.error("Failed to load session storages:", err);
		}
	}

	/**
	 * Creates and caches base resolvers
	 */
	private createBaseResolvers(): void {
		this.baseResolvers = {
			sessionStorageParams: new SessionStorageParamsResolver(this)
		};
	}

	/**
	 * Returns base resolvers
	 */
	public getBaseResolvers(): ISessionStorageServiceBaseResolvers {
		return this.baseResolvers;
	}

	/**
	 * Returns all session storages
	 */
	public getSessionStorage(): ISessionStorageType[] {
		return this.sessionStorage;
	}

	/**
	 * Returns a single session storage type
	 *
	 * @param sessionStorageType Session storage type name
	 */
	public getSessionStorageType(sessionStorageType: string): ISessionStorageType {
		return this.typeIndexByName[sessionStorageType] || null;
	}

	/**
	 * Returns whenever session Storage type exists
	 *
	 * @param sessionStorageType Session storage type name
	 */
	public sessionStorageTypeExists(sessionStorageType: string): boolean {
		return !!this.typeIndexByName[sessionStorageType];
	}
}
